import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { useCreateConversation } from "../../backend/queries/chat";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { PeoplePicker } from "./PeoplePicker";
import { TOUCH_CLOSE } from "./dialogClose";
import { useFocusReturn } from "./useFocusReturn";

type StartConversationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (conversationId: string) => void;
};

/**
 * Staff-only flow for opening a direct message or a group.
 * The people list is the shared `PeoplePicker`.
 */
export function StartConversationDialog({
  open,
  onOpenChange,
  onCreated,
}: StartConversationDialogProps) {
  const focusReturn = useFocusReturn(open);
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createConversation = useCreateConversation();

  useEffect(() => {
    if (open) return;
    setSelected([]);
    setTitle("");
    setError(null);
  }, [open]);

  const isGroup = selected.length > 1;

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    );

  const submit = async () => {
    setError(null);
    if (!selected.length) {
      setError("Choose at least one person.");
      return;
    }
    if (isGroup && !title.trim()) {
      setError("A group needs a name.");
      return;
    }
    try {
      const conversation = await createConversation.mutateAsync(
        isGroup
          ? {
              type: "group",
              title: title.trim(),
              participant_ids: selected,
            }
          : { type: "dm", participant_id: selected[0] },
      );
      onCreated(conversation.id);
      onOpenChange(false);
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : "The conversation could not be started.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
          className="grid-cols-[minmax(0,1fr)] sm:max-w-md"
          closeClassName={TOUCH_CLOSE} {...focusReturn}>
        <DialogHeader>
          <DialogTitle>New conversation</DialogTitle>
          <DialogDescription>
            Pick one person for a direct message, or several to create a group.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <PeoplePicker enabled={open} selected={selected} onToggle={toggle} />

          {isGroup ? (
            <div className="space-y-1.5">
              <Label htmlFor="chat-group-title">Group name</Label>
              <Input
                id="chat-group-title"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Smith relocation"
                maxLength={120}
              />
            </div>
          ) : null}

          {error ? (
            <p role="alert" className="text-sm text-tone-danger-foreground">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="min-h-touch md:min-h-0"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="min-h-touch md:min-h-0"
            onClick={() => void submit()}
            disabled={createConversation.isPending}
          >
            {createConversation.isPending ? (
              <Loader2
                className="mr-2 h-4 w-4 animate-spin"
                aria-hidden="true"
              />
            ) : null}
            {isGroup ? "Create group" : "Start conversation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
